// ============================================================================
// APP - Main Application Component with Authentication
// ============================================================================

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TemplateProvider } from './contexts/TemplateContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminLayout } from './components/AdminLayout';
import { setAccessToken, setAuthFunctions } from './services/api';
import { HelpProvider } from './components/help/HelpContext';
import { HelpButton } from './components/help/HelpButton';
import { HelpPanel } from './components/help/HelpPanel';
import { Toaster } from 'sonner';
import { useTicketLogger } from './hooks/useTicketLogger';
import { SelectionOverlay } from './components/ticket-logger/SelectionOverlay';
import { TicketLoggerModal } from './components/ticket-logger/TicketLoggerModal';

// Pages
import SessionCreator from './pages/SessionCreator';
import LiveSession from './pages/LiveSession';
import AvatarCatalog from './pages/AvatarCatalog';
import CreateAvatar from './pages/CreateAvatar';
import EditAvatar from './pages/EditAvatar';
import AvatarReviewQueue from './pages/AvatarReviewQueue';
import AvatarGroupManagement from './pages/AvatarGroupManagement';
import TemplateReviewQueue from './pages/TemplateReviewQueue';
import CreateAvatarGroup from './pages/CreateAvatarGroup';
import EditAvatarGroup from './pages/EditAvatarGroup';
import TemplateCatalog from './pages/TemplateCatalog';
import PhaseTemplateCatalog from './pages/PhaseTemplateCatalog';
import PhaseTemplateBuilder from './pages/PhaseTemplateBuilder';
import PhaseTemplateDetail from './pages/PhaseTemplateDetail';
import TemplateBuilder from './pages/TemplateBuilder';
import TemplatePreviewPage from './pages/TemplatePreview';
import UserManagement from './pages/UserManagement';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ProjectsDashboard from './pages/ProjectsDashboard';
import ProjectDetail from './pages/ProjectDetail';
import AuditLogViewer from './pages/AuditLogViewer';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import UserProfile from './pages/UserProfile';
import SandboxApprovalPage from './pages/SandboxApprovalPage';
import ReviewsPage from './pages/ReviewsPage';
import { TeamManagement } from './pages/TeamManagement';
import { TeamDetail } from './pages/TeamDetail';
import { TeamCreate } from './pages/TeamCreate';
import BuildWithAI from './pages/BuildWithAI';
import SecondScreenPage from './pages/SecondScreenPage';
import LLMCostsAdmin from './pages/LLMCostsAdmin';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ActiveSessions from './pages/admin/ActiveSessions';
import OpsHealth from './pages/admin/OpsHealth';
import FleetManagement from './pages/FleetManagement';
import MiroTemplateAdmin from './pages/MiroTemplateAdmin';
import RoomManagement from './pages/RoomManagement';
import StudioBookingForm from './pages/StudioBookingForm';
import MyBookings from './pages/MyBookings';
import ReviewQueue from './pages/ReviewQueue';
import AdminFacilitators from './pages/AdminFacilitators';
import AdminEventTypes from './pages/AdminEventTypes';
import StudioCalendar from './pages/StudioCalendar';
import WorkflowConfigAdmin from './pages/WorkflowConfigAdmin';
import ParticipantJoin from './pages/Participant/ParticipantJoin';
import ParticipantView from './pages/Participant/ParticipantView';
import REACHProjectHome from './pages/reach/REACHProjectHome';
import EngagementConfigPage from './pages/reach/EngagementConfigPage';
import StakeholderMapPage from './pages/reach/StakeholderMapPage';
import AssessmentsPage from './pages/reach/AssessmentsPage';
import ResultsDashboardPage from './pages/reach/ResultsDashboardPage';
import { WorkbookPage } from './pages/reach/WorkbookPage';
import CommsLogPage from './pages/reach/CommsLogPage';
import BlockersPage from './pages/reach/BlockersPage';
import { ExtractionReviewPage } from './pages/reach/ExtractionReviewPage';
import AdoptionPlanPage from './pages/reach/AdoptionPlanPage';
import CloseOutPage from './pages/reach/CloseOutPage';
import { REACHFeatureGuard } from './components/reach/common/REACHFeatureGuard';
import Showcase from './pages/Showcase';
import ShowcaseDetail from './pages/ShowcaseDetail';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 120000, // 2 minutes — reduces unnecessary refetches for semi-static data
    },
  },
});

function ConditionalTemplateProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (location.pathname.startsWith('/p/')) return <>{children}</>;
  return <TemplateProvider>{children}</TemplateProvider>;
}

/**
 * App Layout
 * Routes admin/catalog pages through AdminLayout with left sidebar,
 * while session/project/auth pages use their own layouts.
 */
function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const isAuthPage = ['/login', '/auth/callback', '/unauthorized'].includes(location.pathname);
  const isSessionPage = location.pathname.startsWith('/session/');
  const isSecondScreenPage = location.pathname.startsWith('/second-screen/');
  const isParticipantPage = location.pathname.startsWith('/p/');
  const isProjectDetailPage = location.pathname.match(/^\/projects\/[^/]+/);
  const isProfilePage = location.pathname === '/profile';

  // Pages that get the AdminLayout sidebar wrapper (everything except auth, sessions, second-screen, project detail, and profile)
  const needsAdminLayout = isAuthenticated && !isAuthPage && !isSessionPage && !isSecondScreenPage && !isProjectDetailPage && !isProfilePage && !isParticipantPage;

  if (needsAdminLayout) {
    return <AdminLayout>{children}</AdminLayout>;
  }

  return <>{children}</>;
}

/**
 * Admin ticket logger — Ctrl+Shift+T to select any element and log to ADO.
 * Only active for system_admin role. Mounted at root so it works on all pages.
 */
function TicketLogger() {
  const { user } = useAuth();
  const { mode, selected, screenshotBase64, setSelected, cancelSelecting, closeForm } = useTicketLogger();

  if (user?.role !== 'system_admin') return null;

  return (
    <>
      {mode === 'selecting' && (
        <SelectionOverlay onSelect={setSelected} onCancel={cancelSelecting} />
      )}
      {mode === 'form' && selected && (
        <TicketLoggerModal
          selected={selected}
          screenshotBase64={screenshotBase64}
          reportedBy={user.email || user.displayName || 'unknown'}
          onClose={closeForm}
        />
      )}
    </>
  );
}

/**
 * API Service Connector
 * Connects auth context to API service for token injection
 */
function ApiServiceConnector() {
  const { accessToken, refreshToken, logout } = useAuth();

  useEffect(() => {
    // Update token in API service whenever it changes
    setAccessToken(accessToken);
    // Token synced to API service
  }, [accessToken]);

  useEffect(() => {
    // Set auth functions once on mount
    setAuthFunctions(refreshToken, logout);
  }, [refreshToken, logout]);

  return null;
}

/**
 * App Routes
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Participant View — no auth, own QR token */}
      <Route path="/p/:token" element={<ParticipantJoin />} />
      <Route path="/p/:token/session" element={<ParticipantView />} />

      {/* Protected Routes - All Users */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProjectsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/:id"
        element={
          <ProtectedRoute>
            <LiveSession />
          </ProtectedRoute>
        }
      />
      <Route
        path="/second-screen/:id"
        element={
          <ProtectedRoute>
            <SecondScreenPage />
          </ProtectedRoute>
        }
      />

      {/* Showcase Catalog - All Auth Users */}
      <Route
        path="/showcase"
        element={
          <ProtectedRoute>
            <Showcase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/showcase/:showcaseId"
        element={
          <ProtectedRoute>
            <ShowcaseDetail />
          </ProtectedRoute>
        }
      />

      {/* User Profile - All Users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Not Read-Only */}
      <Route
        path="/create"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <SessionCreator />
          </ProtectedRoute>
        }
      />

      {/* Projects - All Users */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsDashboard />
          </ProtectedRoute>
        }
      />
      {/* REACH Module nested inside ProjectDetail shell */}
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        }
      >
        <Route path="reach" element={<REACHFeatureGuard><REACHProjectHome /></REACHFeatureGuard>} />
        <Route path="reach/config" element={<REACHFeatureGuard><EngagementConfigPage /></REACHFeatureGuard>} />
        <Route path="reach/stakeholder-map" element={<REACHFeatureGuard><StakeholderMapPage /></REACHFeatureGuard>} />
        <Route path="reach/assessments" element={<REACHFeatureGuard><AssessmentsPage /></REACHFeatureGuard>} />
        <Route path="reach/results" element={<REACHFeatureGuard><ResultsDashboardPage /></REACHFeatureGuard>} />
        <Route path="reach/workbook" element={<REACHFeatureGuard><WorkbookPage /></REACHFeatureGuard>} />
        <Route path="reach/comms" element={<REACHFeatureGuard><CommsLogPage /></REACHFeatureGuard>} />
        <Route path="reach/blockers" element={<REACHFeatureGuard><BlockersPage /></REACHFeatureGuard>} />
        <Route path="reach/extractions/:sessionId" element={<REACHFeatureGuard><ExtractionReviewPage /></REACHFeatureGuard>} />
        <Route path="reach/plan" element={<REACHFeatureGuard><AdoptionPlanPage /></REACHFeatureGuard>} />
        <Route path="reach/closeout" element={<REACHFeatureGuard><CloseOutPage /></REACHFeatureGuard>} />
      </Route>
      {/* Avatar Catalog - Avatar Admin and System Admin Only */}
      <Route
        path="/avatars"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'system_admin']}>
            <AvatarCatalog />
          </ProtectedRoute>
        }
      />

      {/* Avatar Creation - Not Read-Only */}
      <Route
        path="/avatars/create"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <CreateAvatar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/:avatarId/edit"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <EditAvatar />
          </ProtectedRoute>
        }
      />

      {/* Avatar Administration - Avatar Admin and System Admin Only */}
      <Route
        path="/avatars/review-queue"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'system_admin']}>
            <AvatarReviewQueue />
          </ProtectedRoute>
        }
      />

      {/* Template Administration - Template Admin and System Admin Only */}
      <Route
        path="/templates/review-queue"
        element={
          <ProtectedRoute requiredRoles={['template_admin', 'system_admin']}>
            <TemplateReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'system_admin']}>
            <AvatarGroupManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups/create"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'system_admin']}>
            <CreateAvatarGroup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups/:groupId/edit"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'system_admin']}>
            <EditAvatarGroup />
          </ProtectedRoute>
        }
      />

      {/* Template Management - All Non-Read-Only Users */}
      <Route
        path="/templates"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <TemplateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/create"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <TemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:templateId/preview"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <TemplatePreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:templateId/edit"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <TemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <PhaseTemplateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/create"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <PhaseTemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/:phaseTemplateId"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <PhaseTemplateDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/:phaseTemplateId/edit"
        element={
          <ProtectedRoute requiredRoles={['normal', 'avatar_admin', 'system_admin']}>
            <PhaseTemplateBuilder />
          </ProtectedRoute>
        }
      />


      {/* Build with AI - All authenticated users */}
      <Route
        path="/build"
        element={
          <ProtectedRoute>
            <BuildWithAI />
          </ProtectedRoute>
        }
      />

      {/* Team Management - All authenticated users */}
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <TeamManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/new"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <TeamCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:teamId"
        element={
          <ProtectedRoute>
            <TeamDetail />
          </ProtectedRoute>
        }
      />


      {/* Sandbox Approvals - System Admin only */}
      <Route
        path="/approvals/sandbox"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <SandboxApprovalPage />
          </ProtectedRoute>
        }
      />

      {/* Unified Reviews Page */}
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute requiredRoles={['avatar_admin', 'template_admin', 'system_admin']}>
            <ReviewsPage />
          </ProtectedRoute>
        }
      />

      {/* System Admin Routes */}
      <Route
        path="/admin/fleet"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <FleetManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/rooms"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <RoomManagement />
          </ProtectedRoute>
        }
      />
      {/* Studio Booking — all authenticated users */}
      <Route path="/studio/book" element={<ProtectedRoute><StudioBookingForm /></ProtectedRoute>} />
      <Route path="/studio/bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
      {/* Studio Booking — Tier 1 facilitator + system admin */}
      <Route
        path="/studio/review"
        element={
          <ProtectedRoute requiredRoles={['tier1_facilitator', 'system_admin']}>
            <ReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/calendar"
        element={
          <ProtectedRoute requiredRoles={['tier1_facilitator', 'system_admin']}>
            <StudioCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/facilitators"
        element={
          <ProtectedRoute requiredRoles={['tier1_facilitator', 'system_admin']}>
            <AdminFacilitators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/event-types"
        element={
          <ProtectedRoute requiredRoles={['tier1_facilitator', 'system_admin']}>
            <AdminEventTypes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/llm-costs"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <LLMCostsAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/miro-templates"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <MiroTemplateAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/workflow-config"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <WorkflowConfigAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sessions"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <ActiveSessions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ops"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <OpsHealth />
          </ProtectedRoute>
        }
      />
      {/* Compliance Routes */}
      <Route
        path="/compliance/audit-logs"
        element={
          <ProtectedRoute requiredRoles={['system_admin']}>
            <AuditLogViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

/**
 * Main App Component
 */
export default function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ConditionalTemplateProvider>
            <HelpProvider>
              <ApiServiceConnector />
              <Toaster position="bottom-right" richColors closeButton />
              <AppLayout>
                <AppRoutes />
              </AppLayout>
              <HelpButton />
              <HelpPanel />
              <TicketLogger />
            </HelpProvider>
          </ConditionalTemplateProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
